import { useState, type ReactElement } from 'react';
import type { FieldDefinition, FieldValue } from '@gr/core/content/entities/fields';
import { useT } from '../../../../app/di';
import { Alert } from '../../../../ui-kit/Alert/Alert';
import { Button } from '../../../../ui-kit/Button/Button';
import { FormAlert, useServerForm } from '../../../../ui-kit/form/form';
import type { ServerOutcome } from '../../../../ui-kit/form/server-errors';
import { FormFieldValues } from '../FieldValuesInput/FormFieldValues';
import type { ReferenceLookup } from '../model/reference-search';

export interface EntityRecordValues {
  values: Record<string, FieldValue>;
}

export interface EntityRecordFormProps {
  fields: readonly FieldDefinition[];
  initial: Readonly<Record<string, FieldValue>>;
  references: ReferenceLookup;
  editable: boolean;
  isNew: boolean;
  onSubmit(values: Record<string, FieldValue>): Promise<ServerOutcome>;
}

/** ENT-UC-5, ENT-UC-9.CC-1: значения полей записи; ошибки сервера — у своих полей («values.<ключ>»). */
export function EntityRecordForm({ fields, initial, references, editable, isNew, onSubmit }: EntityRecordFormProps): ReactElement {
  const { t } = useT();
  const [saved, setSaved] = useState(false);
  const { form, submit } = useServerForm<EntityRecordValues>({
    defaultValues: { values: { ...initial } },
    fields: ['values'],
    onSubmit: async ({ values }) => {
      setSaved(false);
      const outcome = await onSubmit(values);
      setSaved(outcome.success && !isNew);
      return outcome;
    },
  });
  const disabled = !editable || form.formState.isSubmitting;
  return (
    <form className="u-stack" onSubmit={submit} noValidate>
      {saved ? <Alert tone="success">{t('common.saved')}</Alert> : null}
      <FormAlert form={form} />
      <FormFieldValues control={form.control} name="values" fields={fields} references={references} disabled={disabled} />
      {editable ? (
        <div>
          <Button type="submit" variant="primary" loading={form.formState.isSubmitting}>
            {isNew ? t('common.create') : t('common.save')}
          </Button>
        </div>
      ) : null}
    </form>
  );
}
